import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useGalleryImages } from "@/hooks/useGalleryImages";
import { Skeleton } from "@/components/ui/skeleton";

const GallerySlideshow = () => {
  const [api, setApi] = useState<any>();
  const [current, setCurrent] = useState(0);
  const { data: galleryImages, isLoading } = useGalleryImages();

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Loading state
  if (isLoading) {
    return (
      <section className="w-full bg-background py-2">
        <div className="w-full max-w-7xl mx-auto px-4">
          <div className="flex gap-3 justify-center">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="w-[300px] h-[225px] rounded-2xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // No images state
  if (!galleryImages || galleryImages.length === 0) {
    return null;
  }

  return (
    <section className="w-full bg-background py-2">
      <Carousel
        setApi={setApi}
        opts={{
          align: "center",
          loop: true,
        }}
        plugins={[
          Autoplay({
            delay: 4000,
            stopOnInteraction: false,
          }),
        ]}
        className="w-full max-w-7xl mx-auto px-4"
      >
        <CarouselContent className="-ml-2 md:-ml-3 items-center">
          {galleryImages.map((image, index) => (
            <CarouselItem
              key={image.id}
              className="pl-2 md:pl-3 basis-2/3 md:basis-1/2 lg:basis-2/5"
            >
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] bg-muted">
                <img
                  src={image.image_url}
                  alt={image.alt_text}
                  width={800}
                  height={600}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-4 md:left-8 bg-background/80 backdrop-blur-sm border-border hover:bg-background" />
        <CarouselNext className="right-4 md:right-8 bg-background/80 backdrop-blur-sm border-border hover:bg-background" />
      </Carousel>

      {/* Dots indicator */}
      <div className="flex justify-center gap-2 mt-3">
        {galleryImages.map((image, index) => (
          <button
            key={image.id}
            onClick={() => api?.scrollTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              current === index
                ? "bg-primary w-6"
                : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default GallerySlideshow;
